import { Collapse, Link, Paper, Typography, useTheme } from '@material-ui/core'
import moment from 'moment'
import { useState } from 'react'

import { getTranslate } from '../../localization'
import { InventoryComparisonRow } from '../../types/inventoryHealth'
import { ComLog } from '../../types/monitoring'
import { statusColor } from './findings'
import { assessEvidence, conversationMix, logsForProvider } from './pmsEvidence'
import useStyles from './styles/InventoryHealthPMSEvidence.styles'

type Props = {
  logs: ComLog[]
  rows: InventoryComparisonRow[]
  loading: boolean
  /** Discriminates this provider's conversations on a multi-provider property. */
  providerURL: string | null
  /**
   * The comlog query itself failed.
   *
   * Kept separate from an empty `logs` because the two support opposite claims.
   * Without it a failed query rendered as "the PMS was never asked" — which is
   * a statement ABOUT THE PMS made from the absence of our own data.
   */
  failed?: boolean
}

/**
 * What the PMS actually said, and whether it reached us.
 *
 * Every other panel on this screen reports STORED state, so a stale number and
 * a number the PMS never sent look identical. This is the only place that can
 * tell them apart, and it is the question support is really asking when they
 * report that a rate "did not go through".
 */
export default function InventoryHealthPMSEvidence({
  logs,
  rows,
  failed = false,
  loading,
  providerURL
}: Props) {
  const classes = useStyles()
  const isDark = useTheme().palette.type === 'dark'
  const [open, setOpen] = useState(false)

  if (loading) {
    return null
  }

  // Checked BEFORE assessEvidence, because that function can only reason about
  // the logs it was given and cannot tell "none exist" from "none arrived".
  if (failed) {
    return (
      <Paper
        className={classes.panel}
        aria-label={getTranslate('PMS evidence')}>
        <div className={classes.headline}>
          <Typography variant="body2">
            {getTranslate(
              'The PMS conversation log could not be read, so this panel cannot say whether the PMS was asked. This is a fault in the log lookup, not evidence about the PMS.'
            )}
          </Typography>
        </div>
      </Paper>
    )
  }

  const scoped = logsForProvider(logs, providerURL)
  const verdict = assessEvidence(scoped, rows)
  // Computed over EVERY conversation in the window, not just the one the
  // verdict is built from. See conversationMix() for why the single-log
  // verdict cannot speak for a multi-channel property.
  const mix = conversationMix(scoped)

  const headline = {
    'no-pull': {
      severity: 'Warning' as const,
      text: getTranslate(
        'No PMS conversation recorded for this property in this window. Nothing here proves the PMS was asked.'
      )
    },
    'pull-failed': {
      severity: 'Critical' as const,
      text: getTranslate(
        'The PMS was asked and did not answer successfully. Everything below this line is the previous answer, however recent it looks.'
      )
    },
    'not-landed': {
      severity: 'Critical' as const,
      text: getTranslate(
        'The PMS answered, but our inventory was not written afterwards. The pipeline stopped between the PMS and us — the values shown are older than the answer.'
      )
    },
    landed: {
      severity: 'Normal' as const,
      text: getTranslate(
        'The PMS answered and our inventory was written afterwards. What you see below is what the PMS told us.'
      )
    }
  }[verdict.kind]

  const log: ComLog | null = verdict.kind === 'no-pull' ? null : verdict.log

  return (
    <Paper className={classes.panel} aria-label={getTranslate('PMS evidence')}>
      <div className={classes.headline}>
        <Typography
          variant="subtitle1"
          style={{ color: statusColor(headline.severity, isDark) }}>
          {headline.text}
        </Typography>
      </div>

      {mix.mixed && (
        <Typography
          variant="body2"
          className={classes.headline}
          style={{ color: statusColor('Warning', isDark) }}>
          {getTranslate(
            '{failed} of {total} conversations in this window failed. The verdict above describes only the most recent one — on a property with several channels the PMS is called once per channel, so this panel cannot yet tell you which channel failed.',
            { failed: mix.failed, total: mix.total }
          )}
        </Typography>
      )}

      {log != null && (
        <>
          <Typography
            variant="caption"
            color="textSecondary"
            className={classes.meta}
            component="div">
            {getTranslate(
              'PMS answered {when} · status {status} · {endpoint}',
              {
                when: moment(log.Date).fromNow(),
                status: log.Status,
                endpoint: log.EndpointName || log.EndpointURL
              }
            )}
            {'storedAt' in verdict &&
              (verdict.storedAt != null ? (
                <>
                  {' · '}
                  {getTranslate('inventory last written {when}', {
                    when: moment(verdict.storedAt).fromNow()
                  })}
                </>
              ) : (
                <>
                  {' · '}
                  {getTranslate('no PMS inventory has ever been written')}
                </>
              ))}
          </Typography>

          {/*
            Collapsed by default. This is the evidence support pastes into a
            ticket, so it has to be reachable — but it is a wall of XML nobody
            reads unless they are already deep in a specific question.
          */}
          <Link
            component="button"
            variant="body2"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}>
            {open
              ? getTranslate('Hide the request and response')
              : getTranslate('Show the request and response')}
          </Link>

          <Collapse in={open}>
            <div className={classes.payloads}>
              <div>
                <Typography variant="caption" color="textSecondary">
                  {getTranslate('Request')}
                </Typography>
                <div className={classes.payload}>{log.Request}</div>
              </div>
              <div>
                <Typography variant="caption" color="textSecondary">
                  {getTranslate('Response')}
                </Typography>
                <div className={classes.payload}>{log.Response}</div>
              </div>
            </div>
          </Collapse>
        </>
      )}
    </Paper>
  )
}
